import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import cookies from 'js-cookie';
import { Button } from '@zocdoc-frontend-common/components-button';
import LanguageIcon from '@zocdoc-frontend-common/assets-icons/LanguageGlobe';
import CloseIcon from '@zocdoc-frontend-common/assets-icons/Close';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { blue100 } from '@zocdoc-mezzanine/foundations-colors';
import {
    labelSmall,
    labelExtraSmall,
} from '@zocdoc-mezzanine/foundations-typography';
import { Metrics, MetricsClickable } from '@zocdoc/frontend-metrics';
import { getLanguageQueryParam } from './getLanguageQueryParam';
import { googleTranslateCookieName } from './constants';

/**
 * There are 3 user flows here:
 * 1. User enters with a query param - user sees translated page with the language selection header bar
 * 2. User enters with a cookie - user sees translated page with the language selection header bar
 * 3. Default - user sees the Language button.  Pressing the button opens the Language dropdown, which can be closed.
 * @param {boolean} showTranslateButton[true] - If true we will show the Translate button, even when cookie and url param are not set.
 * @returns {JSX.Element|null}
 * @constructor
 */
export function TranslationWidget({ showTranslateButton = true }) {
    const [isTranslating, setIsTranslating] = useState(false);
    const [isClosed, setIsClosed] = useState(true);
    const [isTranslateInititalized, setIsTranslateInititalized] = useState(
        false
    );
    const interval = useRef();

    useEffect(() => {
        if (typeof window !== 'undefined') {
            // Set language to auto translate if url param exists - messaging will set this.
            const languageToUse = getLanguageQueryParam();
            if (languageToUse?.trim()?.length > 0) {
                // note: Incognito needs a reload to pick this up.
                cookies.set(googleTranslateCookieName, `/en/${languageToUse}`);
            }

            if (cookies.get(googleTranslateCookieName)?.trim()?.length > 0) {
                // setTimeout is needed to stop React complaining about server-side html not matching client.
                setTimeout(() => {
                    translate();
                }, 1);
            }
        }

        return () => {
            if (interval.current) {
                clearInterval(interval.current);
            }
        };
    }, []);

    useEffect(() => {
        if (isTranslateInititalized) {
            clearInterval(interval.current);
        }
    }, [isTranslateInititalized]);

    function translate() {
        interval.current = setInterval(() => {
            try {
                // eslint-disable-next-line no-undef
                new google.translate.TranslateElement(
                    {
                        pageLanguage: 'en',
                    },
                    'google_translate_element'
                );

                setIsTranslating(true);
                setIsTranslateInititalized(true);
            } catch {}
        }, 500);
    }

    // If showTranslateButton is false, the user will never see the language button or dropdown.
    if (!showTranslateButton) {
        return null;
    }

    // The user has clicked the Language button, or the user already had a language selected on pageload
    if (isTranslating) {
        // If the user closed the footer modal, or the language was selected on pageload, don't render the dropdown
        if (isClosed) {
            return null;
        }
        return (
            <TranslateElementWrapper>
                <TranslateHeader>
                    Choose language
                    <StyledCloseIcon onClick={() => setIsClosed(true)} />
                </TranslateHeader>
                <div id="google_translate_element" />
            </TranslateElementWrapper>
        );
    }

    return (
        <Metrics component="TranslationWidget">
            <MetricsClickable element="TranslateButton">
                <StyledButton
                    data-test="translation-wrapper-button"
                    onClick={() => {
                        translate();
                        setIsClosed(false);
                        setIsTranslating(true);
                    }}
                >
                    <ButtonContent>
                        <StyledLanguageIcon />
                        Language
                    </ButtonContent>
                </StyledButton>
            </MetricsClickable>
        </Metrics>
    );
}

const StyledButton = styled(Button)`
    background-color: #c4c4c4;
    border-color: #c4c4c4;
    border-radius: 18px;
    bottom: 13px;
    color: ${blue100};
    font: ${labelExtraSmall};
    height: 35px;
    padding: 0 20px;
    position: fixed;
    right: 10px;
    z-index: 9999;

    ${breakpoint.small} {
        border-radius: 23px;
        bottom: 20px;
        font: ${labelSmall};
        height: 46px;
        right: 18px;
    }
`;

const StyledLanguageIcon = styled(LanguageIcon)`
    margin: auto 0;
    margin-right: 4px;
`;

const ButtonContent = styled.span`
    display: flex;
`;

const TranslateElementWrapper = styled.div`
    background-color: #c4c4c4;
    bottom: 13px;
    position: fixed;
    right: 10px;
    z-index: 9999;

    ${breakpoint.small} {
        height: 320px;
        right: 18px;
        width: 180px;
    }
`;

const TranslateHeader = styled.div`
    display: flex;
    font: ${labelExtraSmall};
    padding: 10px 10px 0;

    ${breakpoint.small} {
        font: ${labelSmall};
    }
`;

const StyledCloseIcon = styled(CloseIcon)`
    cursor: pointer;
    margin-left: auto;
`;
